import React, { useState } from 'react';
import { Activity, ActivityType, ActivityOutcome, TaskStatus, TaskPriority, activitiesAPI } from '../../services/api';
import {
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ChatBubbleLeftIcon,
  DevicePhoneMobileIcon,
  LinkIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, Button } from '../ui';

interface ActivityFormProps {
  clientId: number;
  dealId?: number;
  onSuccess: () => void;
  onCancel: () => void;
  initialType?: ActivityType;
}

const ActivityForm: React.FC<ActivityFormProps> = ({
  clientId,
  dealId,
  onSuccess,
  onCancel,
  initialType = 'NOTE',
}) => {
  const [formData, setFormData] = useState({
    type: initialType,
    subject: '',
    description: '',
    outcome: '' as ActivityOutcome | '',
    duration: '',
    
    // Task fields
    taskStatus: 'TODO' as TaskStatus,
    taskPriority: 'MEDIUM' as TaskPriority,
    dueDate: '',
    
    // Email fields
    emailTo: '',
    emailFrom: '',
    emailCc: '',
    emailSubject: '',
    
    // Meeting fields
    meetingLocation: '',
    meetingStartTime: '',
    meetingEndTime: '',
    attendees: '',
    
    // General
    isFollowUpRequired: false,
    followUpDate: '',
    tags: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const activityTypes: { value: ActivityType; label: string; icon: React.ReactNode }[] = [
    { value: 'CALL', label: 'Call', icon: <PhoneIcon className="h-5 w-5" /> },
    { value: 'EMAIL', label: 'Email', icon: <EnvelopeIcon className="h-5 w-5" /> },
    { value: 'MEETING', label: 'Meeting', icon: <CalendarIcon className="h-5 w-5" /> },
    { value: 'NOTE', label: 'Note', icon: <DocumentTextIcon className="h-5 w-5" /> },
    { value: 'TASK', label: 'Task', icon: <CheckCircleIcon className="h-5 w-5" /> },
    { value: 'SMS', label: 'SMS', icon: <DevicePhoneMobileIcon className="h-5 w-5" /> },
    { value: 'WHATSAPP', label: 'WhatsApp', icon: <ChatBubbleLeftIcon className="h-5 w-5" /> },
    { value: 'LINKEDIN_MESSAGE', label: 'LinkedIn', icon: <LinkIcon className="h-5 w-5" /> },
  ];

  const outcomes: ActivityOutcome[] = [
    'SUCCESSFUL', 'NO_ANSWER', 'LEFT_VOICEMAIL', 'BUSY', 'WRONG_NUMBER',
    'SCHEDULED_FOLLOWUP', 'NOT_INTERESTED', 'INTERESTED', 'MEETING_SCHEDULED',
    'DEAL_CLOSED', 'DEAL_LOST', 'COMPLETED', 'CANCELLED', 'OTHER'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const activityData: Partial<Activity> = {
        clientId,
        dealId,
        type: formData.type,
        subject: formData.subject,
        description: formData.description || undefined,
        outcome: formData.outcome || undefined,
        duration: formData.duration ? parseInt(formData.duration) : undefined,
        isFollowUpRequired: formData.isFollowUpRequired,
        followUpDate: formData.followUpDate || undefined,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };

      // Add type-specific fields
      if (formData.type === 'TASK') {
        activityData.taskStatus = formData.taskStatus;
        activityData.taskPriority = formData.taskPriority;
        activityData.dueDate = formData.dueDate || undefined;
      }

      if (formData.type === 'EMAIL') {
        activityData.emailTo = formData.emailTo || undefined;
        activityData.emailFrom = formData.emailFrom || undefined;
        activityData.emailCc = formData.emailCc || undefined;
        activityData.emailSubject = formData.emailSubject || undefined;
      }

      if (formData.type === 'MEETING') {
        activityData.meetingLocation = formData.meetingLocation || undefined;
        activityData.meetingStartTime = formData.meetingStartTime || undefined;
        activityData.meetingEndTime = formData.meetingEndTime || undefined;
        activityData.attendees = formData.attendees 
          ? formData.attendees.split(',').map(a => a.trim()).filter(Boolean)
          : [];
      }

      await activitiesAPI.create(activityData);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create activity');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card variant="elevated" className="max-w-3xl mx-auto">
      <CardBody className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-neutral-900">Log Activity</h2>
          <button
            onClick={onCancel}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <Card variant="bordered" className="border-error-200 bg-error-50 mb-4">
            <CardBody className="p-3">
              <p className="text-sm text-error-600">{error}</p>
            </CardBody>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Activity Type Selection */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Activity Type</label>
            <div className="grid grid-cols-4 gap-2">
              {activityTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, type: type.value })}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                    formData.type === type.value
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-neutral-200 hover:border-neutral-300 text-neutral-600'
                  }`}
                >
                  {type.icon}
                  <span className="text-xs mt-1 font-medium">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Subject <span className="text-error-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder="Brief summary of the activity"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder="Additional details..."
            />
          </div>

          {/* Common Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Outcome</label>
              <select
                value={formData.outcome}
                onChange={(e) => setFormData({ ...formData, outcome: e.target.value as ActivityOutcome })}
                className="w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                <option value="">Select outcome...</option>
                {outcomes.map((outcome) => (
                  <option key={outcome} value={outcome}>
                    {outcome.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Duration (minutes)</label>
              <input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                className="w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="e.g., 30"
                min="0"
              />
            </div>
          </div>

          {/* Task-specific fields */}
          {formData.type === 'TASK' && (
            <div className="border-t border-neutral-200 pt-4 space-y-4">
              <h3 className="text-sm font-semibold text-neutral-900">Task Details</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Status</label>
                  <select
                    value={formData.taskStatus}
                    onChange={(e) => setFormData({ ...formData, taskStatus: e.target.value as TaskStatus })}
                    className="w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  >
                    <option value="TODO">To Do</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Priority</label>
                  <select
                    value={formData.taskPriority}
                    onChange={(e) => setFormData({ ...formData, taskPriority: e.target.value as TaskPriority })}
                    className="w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="URGENT">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Due Date</label>
                  <input
                    type="datetime-local"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Email-specific fields */}
          {formData.type === 'EMAIL' && (
            <div className="border-t border-neutral-200 pt-4 space-y-4">
              <h3 className="text-sm font-semibold text-neutral-900">Email Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">To</label>
                  <input
                    type="email"
                    value={formData.emailTo}
                    onChange={(e) => setFormData({ ...formData, emailTo: e.target.value })}
                    className="w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">From</label>
                  <input
                    type="email"
                    value={formData.emailFrom}
                    onChange={(e) => setFormData({ ...formData, emailFrom: e.target.value })}
                    className="w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Meeting-specific fields */}
          {formData.type === 'MEETING' && (
            <div className="border-t border-neutral-200 pt-4 space-y-4">
              <h3 className="text-sm font-semibold text-neutral-900">Meeting Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Start Time</label>
                  <input
                    type="datetime-local"
                    value={formData.meetingStartTime}
                    onChange={(e) => setFormData({ ...formData, meetingStartTime: e.target.value })}
                    className="w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">End Time</label>
                  <input
                    type="datetime-local"
                    value={formData.meetingEndTime}
                    onChange={(e) => setFormData({ ...formData, meetingEndTime: e.target.value })}
                    className="w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Location</label>
                <input
                  type="text"
                  value={formData.meetingLocation}
                  onChange={(e) => setFormData({ ...formData, meetingLocation: e.target.value })}
                  className="w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="Office, Zoom, etc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Attendees (comma-separated)</label>
                <input
                  type="text"
                  value={formData.attendees}
                  onChange={(e) => setFormData({ ...formData, attendees: e.target.value })}
                  className="w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="John Doe, Jane Smith"
                />
              </div>
            </div>
          )}

          {/* Follow-up */}
          <div className="border-t border-neutral-200 pt-4 space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="followUp"
                checked={formData.isFollowUpRequired}
                onChange={(e) => setFormData({ ...formData, isFollowUpRequired: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
              />
              <label htmlFor="followUp" className="ml-2 block text-sm text-neutral-700">
                Follow-up required
              </label>
            </div>

            {formData.isFollowUpRequired && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Follow-up Date</label>
                <input
                  type="datetime-local"
                  value={formData.followUpDate}
                  onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
                  className="w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
              </div>
            )}
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Tags (comma-separated)</label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full rounded-lg border border-neutral-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder="important, follow-up, demo"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-200">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" isLoading={loading}>
              Save Activity
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
};

export default ActivityForm;

